/** Iter 647 — paid-confirmation celebration 🎉 (work-order-paid-celebration-647
 * + addendum 647b). Mounts AFTER the paid state lands (PaidCard), never
 * competing with PayOpeningOverlay. Data = payment-status `celebration`
 * payload — present only when the paid poll ran this visit, so a
 * ?resume= of an already-paid session never celebrates twice. */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  copyTextBeforeNav,
  openOwnComposer,
} from "@/components/engage/SocialPostHelper";

const ORANGE = "#EA580C";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;

/** brand colours like Conifer's #ecede9 are near-white — unreadable as a
 *  number colour on the white card, so fall back to AllSale orange. */
function readableBrand(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return ORANGE;
  const n = parseInt(m[1], 16);
  const lum =
    (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return lum > 0.78 ? ORANGE : `#${m[1]}`;
}

export default function CelebrationOverlay({ c, onClose, shareUrl }) {
  const brand = readableBrand(c.brand_color);
  const saved = Number(c.saved_amount || 0);

  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  const [confettiOn, setConfettiOn] = useState(!reducedMotion);
  useEffect(() => {
    if (!confettiOn) return undefined;
    const t = setTimeout(() => setConfettiOn(false), 2600);
    return () => clearTimeout(t);
  }, [confettiOn]);

  const pieces = useMemo(() => {
    const colors = [c.brand_color || ORANGE, ORANGE, "#FDBA74", "#FFFFFF"];
    return Array.from({ length: 42 }, (_, i) => ({
      left: `${(i * 61) % 100}%`,
      color: colors[i % colors.length],
      delay: `${((i * 37) % 90) / 100}s`,
      dur: `${1.4 + ((i * 13) % 10) / 10}s`,
      drift: `${((i * 29) % 120) - 60}px`,
      rot: `${((i * 83) % 360) + 360}deg`,
    }));
  }, [c.brand_color]);

  // 647b — savings breakdown when points / a reward code contributed.
  const breakdownParts = [];
  if (Number(c.discount_amount) > 0 && (Number(c.points_discount_nzd) > 0 || Number(c.reward_code_discount_nzd) > 0)) {
    breakdownParts.push(`${money(c.discount_amount)} review discount`);
  }
  if (Number(c.points_discount_nzd) > 0) {
    breakdownParts.push(`${money(c.points_discount_nzd)} from ${c.points_redeemed || ""} points`.replace("  ", " "));
  }
  if (Number(c.reward_code_discount_nzd) > 0) {
    breakdownParts.push(`${money(c.reward_code_discount_nzd)} reward code`);
  }

  const streak = c.streak || null;
  const streakLine =
    streak && streak.posts_total > 0
      ? streak.posts_to_next > 0 && streak.next_milestone
        ? `🔥 ${streak.posts_total} visit${streak.posts_total === 1 ? "" : "s"} shared — ${streak.posts_to_next} more to your next milestone`
        : `🔥 ${streak.posts_total} visits shared — legend status!`
      : null;

  const share = (platform) => {
    if (c.share_caption && copyTextBeforeNav(c.share_caption)) {
      toast.success("Caption copied — paste it into your post");
    }
    // Iter 779 — store-specific Facebook card via vendor.share_url.
    openOwnComposer(platform, c.share_caption || "", shareUrl || c.share_url || c.vendor?.share_url);
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.72)" }}
      data-testid="cust-celebration"
    >
      {confettiOn && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden data-testid="cust-cel-confetti">
          {pieces.map((p, i) => (
            <span
              key={i}
              className="absolute -top-4 w-2 h-3 rounded-[2px]"
              style={{
                left: p.left,
                backgroundColor: p.color,
                "--cel-drift": p.drift,
                "--cel-rot": p.rot,
                animation: `cel-fall ${p.dur} ease-in ${p.delay} forwards`,
              }}
            />
          ))}
          <style>{`@keyframes cel-fall { to { transform: translate(var(--cel-drift), 105vh) rotate(var(--cel-rot)); opacity: 0.9; } }`}</style>
        </div>
      )}

      <div className="relative w-full max-w-sm rounded-3xl bg-white shadow-2xl p-6 text-center">
        {saved > 0 ? (
          <div data-testid="cust-cel-saved">
            <div className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">You saved</div>
            <div className="mt-1 font-display text-5xl font-black leading-none" style={{ color: brand }}>
              {money(saved)}
            </div>
            <div className="mt-1.5 text-sm font-bold text-slate-700">at {c.store_name}</div>
          </div>
        ) : (
          <div data-testid="cust-cel-saved">
            <div className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">Payment confirmed</div>
            <div className="mt-1.5 font-display text-xl font-black text-slate-900">
              Thanks for shopping at {c.store_name} 🎉
            </div>
          </div>
        )}
        {breakdownParts.length > 0 && (
          <div className="mt-1.5 text-xs text-slate-500" data-testid="cust-cel-breakdown">
            {breakdownParts.join(" · ")}
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 gap-2">
          <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5 text-xs font-bold text-slate-800" data-testid="cust-cel-points">
            {c.points_pending ? "⏳ points after you post" : `+${c.points_earned || 0} points earned`}
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5 text-xs font-bold text-slate-800" data-testid="cust-cel-balance">
            {c.points_balance ?? 0} balance
            {Number(c.points_purchased_balance) > 0 ? ` · ${c.points_purchased_balance} purchased` : ""}
          </div>
        </div>

        {Number(c.deferred_discount_nzd) > 0 && (
          <div className="mt-2.5 rounded-xl bg-blue-50 border border-blue-200 px-3.5 py-2.5 text-xs font-bold text-blue-700" data-testid="cust-cel-deferred">
            💡 Post your review within 48 h to get {money(c.deferred_discount_nzd)} back as points
          </div>
        )}

        {streakLine && (
          <div className="mt-2.5 rounded-xl bg-orange-50 border border-orange-200 px-3.5 py-2 text-xs font-bold text-orange-700" data-testid="cust-cel-streak">
            {streakLine}
          </div>
        )}

        <div className="mt-4">
          <div className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">Share your win</div>
          <div className="mt-2 flex justify-center gap-2">
            <SharePill platform="instagram" label="📸 Instagram" onTap={share} />
            <SharePill platform="facebook" label="👍 Facebook" onTap={share} />
            <SharePill platform="tiktok" label="🎵 TikTok" onTap={share} />
          </div>
          {c.share_caption && (
            <p className="mt-2 text-[11px] text-slate-400 leading-relaxed" data-testid="cust-cel-caption">
              {c.share_caption}
            </p>
          )}
        </div>

        <button
          type="button"
          data-testid="cust-cel-continue"
          onClick={onClose}
          className="mt-4 w-full rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold py-3 transition-colors"
        >
          Continue
        </button>
      </div>
    </div>
  );
}

function SharePill({ platform, label, onTap }) {
  return (
    <button
      type="button"
      data-testid={`cust-cel-share-${platform}`}
      onClick={() => onTap(platform)}
      className="rounded-full bg-white border border-slate-300 hover:border-slate-500 text-slate-800 text-xs font-bold px-3.5 py-2 transition-colors"
    >
      {label}
    </button>
  );
}
